import React, { useRef } from 'react';
import { useFormikContext } from 'formik';
import { intersection, isEqual } from 'lodash';

import { ErrorHelperTextFormik } from '~/components/core/Formik/ErrorHelperTextFormik';
import { useFormikFieldError } from '~/components/core/Formik/formikErrorUtils';
import useOrganization from '~/components/OrganizationContext';
import LobFilterMultiSelect from '~/components/TPA/LOB/LobFilterMultiSelect';
import SubOrganizationMultiselectFormik from '~/components/TPA/SubOrganizations/SubOrganizationMultiselectFormik';

import type { ScopeValues } from './scopeFields';
import SCOPE_TEXTS from './texts';

/** The sub-org and LoB scope pickers, for a Formik form whose values include `ScopeValues`. */
const ScopeFieldsFormik: React.FC = () => {
  const { values, isSubmitting, setFieldValue } = useFormikContext<ScopeValues>();
  const lobsFieldError = useFormikFieldError('lobs');
  const { subOrganizations, subOrganizationEnabled } = useOrganization() as {
    subOrganizations?: { id: number; name: string }[];
    subOrganizationEnabled?: boolean;
  };

  const allSubOrgIds = subOrganizationEnabled ? (subOrganizations ?? []).map((subOrg) => subOrg.id) : [];
  const selectedSubOrgIds = values.is_all_sub_orgs ? allSubOrgIds : values.sub_organization_ids;

  // Prune explicitly-picked LoBs the offered set no longer contains — but never on the first settle, so a
  // stored scope holding a LoB its sub-orgs no longer support survives opening the form untouched. An
  // all-LoBs scope is left alone: its universe is org-wide, not the sub-org-filtered option set. A prune
  // that would empty the list normalizes to All instead — an empty LoB axis is unrepresentable (the menu
  // renders an empty selection as All), so the state must actually BE All.
  // The offered set arrives via `LobFilterMultiSelect`'s `onOptionsChange`: the picker owns the one
  // `useLobOptions` fetch, so opening the dialog costs a single GET and the prune always runs against
  // exactly the set the menu renders.
  const hasLoadedLobOptions = useRef(false);
  const handleLobOptionsChange = (offeredLobs: string[]) => {
    const isInitialLoad = !hasLoadedLobOptions.current;
    hasLoadedLobOptions.current = true;
    if (isInitialLoad || values.is_all_lobs) {
      return;
    }
    const prunedLobs = intersection(values.lobs, offeredLobs);
    if (prunedLobs.length === 0) {
      setFieldValue('is_all_lobs', true);
      setFieldValue('lobs', []);
    } else if (!isEqual(values.lobs, prunedLobs)) {
      setFieldValue('lobs', prunedLobs);
    }
  };

  // The same "All" contract as the claims-screen filter this select mirrors: All is checked alone, picking
  // an individual LoB unticks it, and an emptied selection reads as All — so the LoB axis is never empty.
  const lobSelectValue = values.is_all_lobs ? [SCOPE_TEXTS.selectAllLobs] : values.lobs;
  const handleLobsChange = (newValues: string[]) => {
    if (newValues.includes(SCOPE_TEXTS.selectAllLobs) || newValues.length === 0) {
      setFieldValue('is_all_lobs', true);
      setFieldValue('lobs', []);
    } else {
      setFieldValue('is_all_lobs', false);
      setFieldValue('lobs', newValues);
    }
  };

  return (
    <>
      {subOrganizationEnabled ? (
        <SubOrganizationMultiselectFormik
          subOrganizationsFieldId="sub_organization_ids"
          allSelectedFieldId="is_all_sub_orgs"
          selectAllLabel={SCOPE_TEXTS.selectAllSubOrgs}
          showAllInRenderedSelectedOptions
          shouldDisplayAllOption
          emptySelectionFallsBackToAll
          disabled={isSubmitting}
        />
      ) : null}
      <div>
        <LobFilterMultiSelect
          id="lobs"
          label={SCOPE_TEXTS.lobsLabel}
          fullWidth
          value={lobSelectValue}
          onChange={handleLobsChange}
          onOptionsChange={handleLobOptionsChange}
          subOrganizationIds={selectedSubOrgIds}
          allOptionValue={SCOPE_TEXTS.selectAllLobs}
          error={lobsFieldError.hasError}
          disabled={isSubmitting}
        />
        <ErrorHelperTextFormik id="lobs" />
      </div>
    </>
  );
};

export default ScopeFieldsFormik;
